import React, { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import {
  X, Download, Edit, Key, Save, Search, Filter,
  Calendar, User, Mail, Phone, Shield, DollarSign, FileText, Check, XCircle, Gift
} from 'lucide-react';
import { toast } from '../hooks/use-toast';
import { adminAPI } from '../services/api';

// Users Modal Component
export const UsersModal = ({ isOpen, onClose, users, onRefresh }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [editingUser, setEditingUser] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', email: '', phone: '' });
  const [resettingPassword, setResettingPassword] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [rejectingDriverId, setRejectingDriverId] = useState(null);
  const [rejectReason, setRejectReason] = useState('');

  const filteredUsers = users.filter(user => {
    const matchesSearch = user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         user.email?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRole = roleFilter === 'all' || user.role === roleFilter;
    return matchesSearch && matchesRole;
  });

  const handleExportCSV = () => {
    const headers = ['Name', 'Email', 'Role', 'Phone', 'Registration Date', 'Credit Balance'];
    const rows = filteredUsers.map(u => [
      u.name, u.email, u.role, u.phone || 'N/A', 
      u.created_at ? new Date(u.created_at).toLocaleDateString() : 'N/A',
      `£${u.credit_balance?.toFixed(2) || '0.00'}`
    ]);
    
    const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `wheely-users-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    
    toast({ title: 'Export Successful', description: 'Users data exported to CSV' });
  };

  const handleEdit = (user) => {
    setEditingUser(user.id);
    setEditForm({
      name: user.name,
      email: user.email,
      phone: user.phone || ''
    });
  };

  const handleSaveEdit = async () => {
    try {
      await adminAPI.updateUser(editingUser, editForm);
      toast({ title: 'Success', description: 'User updated successfully' });
      setEditingUser(null);
      onRefresh();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to update user',
        variant: 'destructive'
      });
    }
  };

  const handleResetPassword = async (userId) => {
    if (!newPassword || newPassword.length < 6) {
      toast({
        title: 'Invalid Password',
        description: 'Password must be at least 6 characters',
        variant: 'destructive'
      });
      return;
    }

    try {
      const response = await adminAPI.resetPassword(userId, newPassword);
      toast({
        title: 'Password Reset Successful',
        description: `Temporary password: ${response.temporary_password}. Share this with the user.`,
        duration: 10000
      });
      setResettingPassword(null);
      setNewPassword('');
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to reset password',
        variant: 'destructive'
      });
    }
  };

  const handleViewDoc = async (driverId, docType) => {
    try {
      await adminAPI.viewVerificationDocument(driverId, docType);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Could not load document',
        variant: 'destructive'
      });
    }
  };

  const handleApproveDriver = async (driverId, name) => {
    try {
      await adminAPI.approveDriver(driverId);
      toast({ title: 'Driver Approved', description: `${name} can now go online` });
      onRefresh();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to approve driver',
        variant: 'destructive'
      });
    }
  };

  const handleRejectDriver = async (driverId) => {
    if (!rejectReason.trim()) {
      toast({ title: 'Reason required', description: 'Please explain why this is being rejected', variant: 'destructive' });
      return;
    }
    try {
      await adminAPI.rejectDriver(driverId, rejectReason);
      toast({ title: 'Driver Rejected' });
      setRejectingDriverId(null);
      setRejectReason('');
      onRefresh();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to reject driver',
        variant: 'destructive'
      });
    }
  };

  const handleSetMembership = async (driverId, tier, driverName) => {
    try {
      await adminAPI.setDriverMembership(driverId, tier || null);
      toast({
        title: 'Membership updated',
        description: tier ? `${driverName} is now on ${tier} (no charge)` : `${driverName}'s membership cleared`
      });
      onRefresh();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to update membership',
        variant: 'destructive'
      });
    }
  };

  const membershipBadgeColor = (tier) => (
    tier === 'gold' ? '#facc15' : tier === 'silver' ? '#94a3b8' : tier === 'bronze' ? '#EB9868' : '#6b7280'
  );

  const verificationBadgeStyle = (status) => ({
    background: status === 'approved' ? '#10b981'
      : status === 'pending' ? '#E17431'
      : status === 'rejected' ? '#ef4444'
      : '#6b7280'
  });

  const handleDateFilter = async () => {
    try {
      const data = await adminAPI.getAllUsers(
        roleFilter === 'all' ? null : roleFilter,
        fromDate,
        toDate
      );
      onRefresh(data.users);
      toast({ title: 'Filters Applied', description: `Showing ${data.users.length} users` });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to apply filters',
        variant: 'destructive'
      });
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto" style={{ background: 'rgba(15, 23, 42, 0.98)', borderColor: 'rgba(46, 178, 213, 0.3)' }}>
        <DialogHeader>
          <DialogTitle className="text-white text-2xl flex items-center gap-2">
            <User className="w-6 h-6 text-wheely-orange-light" />
            Registered Users ({filteredUsers.length})
          </DialogTitle>
          <DialogDescription className="text-gray-400">
            View and manage all registered users
          </DialogDescription>
        </DialogHeader>

        {/* Filters */}
        <div className="space-y-4 mb-4">
          <div className="flex gap-4 flex-wrap">
            <div className="flex-1 min-w-[200px]">
              <Label className="text-gray-300">Search</Label>
              <div className="relative">
                <Search className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Search by name or email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 bg-wheely-navy-900 border-wheely-orange/30/30 text-white"
                />
              </div>
            </div>

            <div>
              <Label className="text-gray-300">Role Filter</Label>
              <select
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value)}
                className="w-full h-10 px-3 rounded-md bg-wheely-navy-900 border border-wheely-orange/30/30 text-white"
              >
                <option value="all">All Roles</option>
                <option value="customer">Customers</option>
                <option value="driver">Drivers</option>
                <option value="admin">Admins</option>
              </select>
            </div>
          </div>

          <div className="flex gap-4 items-end">
            <div className="flex-1">
              <Label className="text-gray-300">From Date</Label>
              <Input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                className="bg-wheely-navy-900 border-wheely-orange/30/30 text-white"
              />
            </div>
            <div className="flex-1">
              <Label className="text-gray-300">To Date</Label>
              <Input
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                className="bg-wheely-navy-900 border-wheely-orange/30/30 text-white"
              />
            </div>
            <Button
              onClick={handleDateFilter}
              className="bg-wheely-orange/20 hover:bg-wheely-orange/20"
            >
              <Filter className="w-4 h-4 mr-2" />
              Apply
            </Button>
            <Button
              onClick={handleExportCSV}
              className="bg-green-600 hover:bg-green-700"
            >
              <Download className="w-4 h-4 mr-2" />
              Export CSV
            </Button>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-wheely-orange/30/30">
                <th className="text-left p-3 text-gray-300">Name</th>
                <th className="text-left p-3 text-gray-300">Email</th>
                <th className="text-left p-3 text-gray-300">Role</th>
                <th className="text-left p-3 text-gray-300">Documents</th>
                <th className="text-left p-3 text-gray-300">Membership</th>
                <th className="text-left p-3 text-gray-300">Phone</th>
                <th className="text-left p-3 text-gray-300">Registered</th>
                <th className="text-left p-3 text-gray-300">Credits</th>
                <th className="text-left p-3 text-gray-300">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map((user) => (
                <tr key={user.id} className="border-b border-wheely-navy-700 hover:bg-wheely-navy-900/50">
                  <td className="p-3">
                    {editingUser === user.id ? (
                      <Input
                        value={editForm.name}
                        onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                        className="bg-wheely-navy-800 border-wheely-orange/30/30 text-white"
                      />
                    ) : (
                      <span className="text-white">{user.name}</span>
                    )}
                  </td>
                  <td className="p-3">
                    {editingUser === user.id ? (
                      <Input
                        type="email"
                        value={editForm.email}
                        onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                        className="bg-wheely-navy-800 border-wheely-orange/30/30 text-white"
                      />
                    ) : (
                      <span className="text-gray-300">{user.email}</span>
                    )}
                  </td>
                  <td className="p-3">
                    <Badge
                      style={{
                        background: user.role === 'admin' ? '#E17431' : user.role === 'driver' ? '#10b981' : '#2EB2D5'
                      }}
                    >
                      {user.role}
                    </Badge>
                  </td>
                  <td className="p-3">
                    {user.role === 'driver' ? (
                      <div className="flex flex-col gap-1.5">
                        <Badge style={verificationBadgeStyle(user.verification_status)}>
                          {user.verification_status}
                        </Badge>
                        {(user.has_license_front || user.has_license_back || user.has_insurance_document) && (
                          <div className="flex gap-1 flex-wrap">
                            {user.has_license_front && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleViewDoc(user.id, 'license_front')}
                                style={{ borderColor: 'rgba(225, 116, 49, 0.4)', color: '#E17431' }}
                              >
                                <FileText className="w-3 h-3 mr-1" /> Licence front
                              </Button>
                            )}
                            {user.has_license_back && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleViewDoc(user.id, 'license_back')}
                                style={{ borderColor: 'rgba(225, 116, 49, 0.4)', color: '#E17431' }}
                              >
                                <FileText className="w-3 h-3 mr-1" /> Licence back
                              </Button>
                            )}
                            {user.has_insurance_document && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleViewDoc(user.id, 'insurance')}
                                style={{ borderColor: 'rgba(46, 178, 213, 0.4)', color: '#2EB2D5' }}
                              >
                                <FileText className="w-3 h-3 mr-1" /> Insurance
                              </Button>
                            )}
                          </div>
                        )}
                        {user.verification_status === 'pending' && rejectingDriverId !== user.id && (
                          <div className="flex gap-1">
                            <Button
                              size="sm"
                              onClick={() => handleApproveDriver(user.id, user.name)}
                              className="bg-green-600 hover:bg-green-700"
                            >
                              <Check className="w-3 h-3 mr-1" /> Approve
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setRejectingDriverId(user.id)}
                              style={{ borderColor: 'rgba(239, 68, 68, 0.5)', color: '#ef4444' }}
                            >
                              <XCircle className="w-3 h-3 mr-1" /> Reject
                            </Button>
                          </div>
                        )}
                        {rejectingDriverId === user.id && (
                          <div className="flex flex-col gap-1.5 mt-1 p-2 rounded" style={{ background: 'rgba(15, 23, 42, 0.8)' }}>
                            <Input
                              placeholder="Reason for rejection..."
                              value={rejectReason}
                              onChange={(e) => setRejectReason(e.target.value)}
                              className="bg-wheely-navy-800 border-wheely-navy-700 text-white text-xs h-8"
                            />
                            <div className="flex gap-1">
                              <Button
                                size="sm"
                                onClick={() => handleRejectDriver(user.id)}
                                className="bg-red-600 hover:bg-red-700"
                              >
                                Confirm reject
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => { setRejectingDriverId(null); setRejectReason(''); }}
                                className="border-wheely-navy-700"
                              >
                                Cancel
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      <span className="text-gray-500">—</span>
                    )}
                  </td>
                  <td className="p-3">
                    {user.role === 'driver' ? (
                      <div className="flex flex-col gap-1.5">
                        <select
                          value={user.membership_tier === 'none' ? '' : user.membership_tier || ''}
                          onChange={(e) => handleSetMembership(user.id, e.target.value, user.name)}
                          className="bg-wheely-navy-800 border border-wheely-orange/30/30 text-white text-xs rounded px-2 py-1"
                        >
                          <option value="">No membership</option>
                          <option value="bronze">Bronze</option>
                          <option value="silver">Silver</option>
                          <option value="gold">Gold</option>
                        </select>
                        {user.membership_tier && user.membership_tier !== 'none' && (
                          <Badge style={{ background: membershipBadgeColor(user.membership_tier) }}>
                            {user.membership_tier}
                          </Badge>
                        )}
                        {user.membership_admin_granted && (
                          <Badge style={{ background: 'rgba(139, 92, 246, 0.3)', color: '#c4b5fd' }}>
                            <Gift className="w-3 h-3 mr-1 inline" /> Admin granted
                          </Badge>
                        )}
                      </div>
                    ) : (
                      <span className="text-gray-500">—</span>
                    )}
                  </td>
                  <td className="p-3">
                    {editingUser === user.id ? (
                      <Input
                        value={editForm.phone}
                        onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                        className="bg-wheely-navy-800 border-wheely-orange/30/30 text-white"
                      />
                    ) : (
                      <span className="text-gray-300">{user.phone || 'N/A'}</span>
                    )}
                  </td>
                  <td className="p-3 text-gray-300 text-sm">
                    {user.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}
                  </td>
                  <td className="p-3 text-green-400">
                    £{user.credit_balance?.toFixed(2) || '0.00'}
                  </td>
                  <td className="p-3">
                    <div className="flex gap-2">
                      {editingUser === user.id ? (
                        <>
                          <Button
                            size="sm"
                            onClick={handleSaveEdit}
                            className="bg-green-600 hover:bg-green-700"
                          >
                            <Save className="w-3 h-3" />
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => setEditingUser(null)}
                            variant="outline"
                            className="border-wheely-navy-700"
                          >
                            <X className="w-3 h-3" />
                          </Button>
                        </>
                      ) : (
                        <>
                          <Button
                            size="sm"
                            onClick={() => handleEdit(user)}
                            className="bg-wheely-orange/20 hover:bg-wheely-orange/20"
                          >
                            <Edit className="w-3 h-3" />
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => setResettingPassword(user.id)}
                            className="bg-wheely-orange-dark hover:bg-orange-700"
                          >
                            <Key className="w-3 h-3" />
                          </Button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Password Reset Modal */}
        {resettingPassword && (
          <div className="fixed inset-0 bg-wheely-navy-950/50 flex items-center justify-center z-50">
            <div className="bg-wheely-navy-900 p-6 rounded-lg border border-wheely-orange/30/30 max-w-md w-full mx-4">
              <h3 className="text-white text-lg font-bold mb-4">Reset Password</h3>
              <Label className="text-gray-300">New Temporary Password</Label>
              <Input
                type="text"
                placeholder="Enter new password (min 6 characters)"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="bg-wheely-navy-800 border-wheely-orange/30/30 text-white mb-4"
              />
              <div className="flex gap-2">
                <Button
                  onClick={() => handleResetPassword(resettingPassword)}
                  className="flex-1 bg-wheely-orange-dark hover:bg-orange-700"
                >
                  Reset Password
                </Button>
                <Button
                  onClick={() => {
                    setResettingPassword(null);
                    setNewPassword('');
                  }}
                  variant="outline"
                  className="border-wheely-navy-700"
                >
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

// Active Jobs Modal Component
export const ActiveJobsModal = ({ isOpen, onClose, jobs, onRefresh }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [refundJob, setRefundJob] = useState(null);
  const [refundAmount, setRefundAmount] = useState('');
  const [refundNote, setRefundNote] = useState('');
  const [refunding, setRefunding] = useState(false);

  const startRefund = (job) => {
    setRefundJob(job);
    setRefundAmount(job.price != null ? job.price.toFixed(2) : '');
    setRefundNote('');
  };

  const submitRefund = async () => {
    const amount = parseFloat(refundAmount);
    if (Number.isNaN(amount) || amount < 0 || amount > (refundJob.price || 0)) {
      toast({ title: 'Check the amount', description: `Enter between £0 and £${(refundJob.price || 0).toFixed(2)}`, variant: 'destructive' });
      return;
    }
    setRefunding(true);
    try {
      const result = await adminAPI.refundServiceRequest(refundJob.id, amount, refundNote || null);
      toast({ title: 'Job cancelled', description: result.message });
      setRefundJob(null);
      if (onRefresh) await onRefresh();
    } catch (error) {
      toast({ title: 'Refund failed', description: error.response?.data?.detail || 'Please try again', variant: 'destructive' });
    } finally {
      setRefunding(false);
    }
  };

  const filteredJobs = jobs.filter(job =>
    job.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    job.driver_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    job.location?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleExportCSV = () => {
    const headers = ['Job ID', 'Customer', 'Driver', 'Service', 'Status', 'Location', 'Price', 'Created'];
    const rows = filteredJobs.map(j => [
      j.id.substring(0, 8), j.customer_name, j.driver_name, j.service_type,
      j.status, j.location, `£${j.price?.toFixed(2) || '0.00'}`,
      j.created_at ? new Date(j.created_at).toLocaleDateString() : 'N/A'
    ]);
    
    const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `wheely-active-jobs-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    
    toast({ title: 'Export Successful', description: 'Active jobs exported to CSV' });
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto" style={{ background: 'rgba(15, 23, 42, 0.98)', borderColor: 'rgba(235, 152, 104, 0.3)' }}>
        <DialogHeader>
          <DialogTitle className="text-white text-2xl flex items-center gap-2">
            <Shield className="w-6 h-6 text-wheely-orange-light" />
            Active Jobs ({filteredJobs.length})
          </DialogTitle>
          <DialogDescription className="text-gray-400">
            All ongoing service requests
          </DialogDescription>
        </DialogHeader>

        {/* Filters */}
        <div className="flex gap-4 mb-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Search jobs..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 bg-wheely-navy-900 border-wheely-orange/30 text-white"
              />
            </div>
          </div>
          <Button
            onClick={handleExportCSV}
            className="bg-green-600 hover:bg-green-700"
          >
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-wheely-orange/30">
                <th className="text-left p-3 text-gray-300">Customer</th>
                <th className="text-left p-3 text-gray-300">Driver</th>
                <th className="text-left p-3 text-gray-300">Service</th>
                <th className="text-left p-3 text-gray-300">Status</th>
                <th className="text-left p-3 text-gray-300">Location</th>
                <th className="text-left p-3 text-gray-300">Vehicle</th>
                <th className="text-left p-3 text-gray-300">Price</th>
                <th className="text-left p-3 text-gray-300">Created</th>
                <th className="text-left p-3 text-gray-300">Cancellation</th>
              </tr>
            </thead>
            <tbody>
              {filteredJobs.map((job) => (
                <React.Fragment key={job.id}>
                <tr className="border-b border-wheely-navy-700 hover:bg-wheely-navy-900/50">
                  <td className="p-3 text-white">{job.customer_name}</td>
                  <td className="p-3 text-gray-300">{job.driver_name}</td>
                  <td className="p-3">
                    <Badge className="bg-wheely-orange/20">{job.service_type}</Badge>
                  </td>
                  <td className="p-3">
                    <Badge
                      style={{
                        background: job.status === 'in_progress' ? '#10b981' :
                                  job.status === 'pending' ? '#f59e0b' : '#2EB2D5'
                      }}
                    >
                      {job.status}
                    </Badge>
                  </td>
                  <td className="p-3 text-gray-300 text-sm">{job.location}</td>
                  <td className="p-3 text-gray-300 text-sm">{job.vehicle_model || 'N/A'}</td>
                  <td className="p-3 text-green-400">
                    {job.price ? `£${job.price.toFixed(2)}` : 'TBD'}
                  </td>
                  <td className="p-3 text-gray-400 text-sm">
                    {job.created_at ? new Date(job.created_at).toLocaleString() : 'N/A'}
                  </td>
                  <td className="p-3 text-sm">
                    {job.cancellation_requested_at && (
                      <div className="mb-2">
                        <Badge className="bg-red-600">Customer cancelled</Badge>
                        {job.cancellation_reason && <p className="text-gray-400 mt-1">"{job.cancellation_reason}"</p>}
                      </div>
                    )}
                    {job.status === 'in_progress' && job.paid && (
                      <Button size="sm" variant="outline" onClick={() => startRefund(job)} className="border-red-500/50 text-red-400">
                        Cancel &amp; refund
                      </Button>
                    )}
                  </td>
                </tr>
                {refundJob?.id === job.id && (
                  <tr className="border-b border-wheely-navy-700 bg-wheely-navy-900/70">
                    <td colSpan={9} className="p-4">
                      <div className="space-y-3 max-w-2xl">
                        <p className="text-gray-300 text-sm">
                          Terms §6: refund in full if the driver hadn't set off. If they had, refund the price minus the
                          service provided so far (e.g. their travel). The driver keeps their share of whatever isn't refunded.
                          This cancels the job and can't be undone.
                        </p>
                        <div className="flex gap-3 items-end flex-wrap">
                          <div>
                            <Label className="text-white">Refund (£, max {(job.price || 0).toFixed(2)})</Label>
                            <Input type="number" step="0.01" min="0" max={job.price || 0} value={refundAmount}
                              onChange={(e) => setRefundAmount(e.target.value)}
                              className="bg-wheely-navy-900 border-wheely-orange/30 text-white w-36" />
                          </div>
                          <div className="flex-1 min-w-[200px]">
                            <Label className="text-white">Note to customer (optional)</Label>
                            <Input value={refundNote} maxLength={500} onChange={(e) => setRefundNote(e.target.value)}
                              placeholder="e.g. Driver had driven 10 miles to you"
                              className="bg-wheely-navy-900 border-wheely-orange/30 text-white" />
                          </div>
                          <Button variant="outline" onClick={() => setRefundJob(null)} disabled={refunding}>Back</Button>
                          <Button onClick={submitRefund} disabled={refunding} className="bg-red-600 hover:bg-red-700">
                            {refunding ? 'Refunding...' : `Cancel job & refund £${parseFloat(refundAmount || 0).toFixed(2)}`}
                          </Button>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Completed Jobs Modal Component
export const CompletedJobsModal = ({ isOpen, onClose, jobs, summary }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  const filteredJobs = jobs.filter(job =>
    job.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    job.driver_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    job.service_type?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleExportCSV = () => {
    const headers = ['Job ID', 'Customer', 'Driver', 'Service', 'Total Price', 'Commission', 'Driver Earnings', 'Tier', 'Completed'];
    const rows = filteredJobs.map(j => [
      j.id.substring(0, 8), j.customer_name, j.driver_name, j.service_type,
      `£${j.total_price?.toFixed(2)}`, `£${j.commission?.toFixed(2)}`, 
      `£${j.driver_earnings?.toFixed(2)}`, j.membership_tier,
      j.completed_at ? new Date(j.completed_at).toLocaleDateString() : 'N/A'
    ]);
    
    const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `wheely-completed-jobs-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    
    toast({ title: 'Export Successful', description: 'Completed jobs exported to CSV' });
  };

  const handleDateFilter = async () => {
    try {
      const data = await adminAPI.getCompletedJobs(fromDate, toDate);
      onClose();
      // Re-open with new data
      toast({ 
        title: 'Filters Applied', 
        description: `Showing ${data.completed_jobs.length} completed jobs` 
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to apply filters',
        variant: 'destructive'
      });
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-7xl max-h-[90vh] overflow-y-auto" style={{ background: 'rgba(15, 23, 42, 0.98)', borderColor: 'rgba(139, 92, 246, 0.3)' }}>
        <DialogHeader>
          <DialogTitle className="text-white text-2xl flex items-center gap-2">
            <DollarSign className="w-6 h-6 text-purple-400" />
            Completed Jobs - Revenue History ({filteredJobs.length})
          </DialogTitle>
          <DialogDescription className="text-gray-400">
            Total Revenue: £{summary?.total_revenue?.toFixed(2) || '0.00'} | 
            Commission: £{summary?.total_commission?.toFixed(2) || '0.00'} | 
            Driver Earnings: £{summary?.total_driver_earnings?.toFixed(2) || '0.00'}
          </DialogDescription>
        </DialogHeader>

        {/* Filters */}
        <div className="space-y-4 mb-4">
          <div className="flex gap-4 flex-wrap">
            <div className="flex-1 min-w-[200px]">
              <Label className="text-gray-300">Search</Label>
              <div className="relative">
                <Search className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Search by customer, driver, or service..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 bg-wheely-navy-900 border-purple-500/30 text-white"
                />
              </div>
            </div>
          </div>

          <div className="flex gap-4 items-end">
            <div className="flex-1">
              <Label className="text-gray-300">From Date</Label>
              <Input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                className="bg-wheely-navy-900 border-purple-500/30 text-white"
              />
            </div>
            <div className="flex-1">
              <Label className="text-gray-300">To Date</Label>
              <Input
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                className="bg-wheely-navy-900 border-purple-500/30 text-white"
              />
            </div>
            <Button
              onClick={handleDateFilter}
              className="bg-purple-600 hover:bg-purple-700"
            >
              <Filter className="w-4 h-4 mr-2" />
              Apply
            </Button>
            <Button
              onClick={handleExportCSV}
              className="bg-green-600 hover:bg-green-700"
            >
              <Download className="w-4 h-4 mr-2" />
              Export CSV
            </Button>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-purple-500/30">
                <th className="text-left p-3 text-gray-300">Customer</th>
                <th className="text-left p-3 text-gray-300">Driver</th>
                <th className="text-left p-3 text-gray-300">Service</th>
                <th className="text-left p-3 text-gray-300">Location</th>
                <th className="text-left p-3 text-gray-300">Total Price</th>
                <th className="text-left p-3 text-gray-300">Commission</th>
                <th className="text-left p-3 text-gray-300">Driver Earned</th>
                <th className="text-left p-3 text-gray-300">Tier</th>
                <th className="text-left p-3 text-gray-300">Completed</th>
              </tr>
            </thead>
            <tbody>
              {filteredJobs.map((job) => (
                <tr key={job.id} className="border-b border-wheely-navy-700 hover:bg-wheely-navy-900/50">
                  <td className="p-3 text-white">{job.customer_name}</td>
                  <td className="p-3 text-gray-300">{job.driver_name}</td>
                  <td className="p-3">
                    <Badge className="bg-wheely-orange/20">{job.service_type}</Badge>
                  </td>
                  <td className="p-3 text-gray-300 text-sm">{job.location}</td>
                  <td className="p-3 text-white font-semibold">£{job.total_price?.toFixed(2)}</td>
                  <td className="p-3 text-green-400">£{job.commission?.toFixed(2)}</td>
                  <td className="p-3 text-wheely-orange-light">£{job.driver_earnings?.toFixed(2)}</td>
                  <td className="p-3">
                    <Badge
                      style={{
                        background: job.membership_tier === 'gold' ? '#facc15' :
                                  job.membership_tier === 'silver' ? '#94a3b8' : '#EB9868'
                      }}
                    >
                      {job.membership_tier}
                    </Badge>
                  </td>
                  <td className="p-3 text-gray-400 text-sm">
                    {job.completed_at ? new Date(job.completed_at).toLocaleString() : 'N/A'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Online Drivers Modal Component
export const OnlineDriversModal = ({ isOpen, onClose, drivers }) => {
  const [searchTerm, setSearchTerm] = useState('');

  const filteredDrivers = drivers.filter(driver =>
    driver.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    driver.location?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleExportCSV = () => {
    const headers = ['Name', 'Location', 'Membership', 'Rating', 'Total Jobs', 'Status'];
    const rows = filteredDrivers.map(d => [
      d.name, d.location, d.membership_tier, d.rating, d.total_jobs, 'Online'
    ]);
    
    const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `wheely-online-drivers-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    
    toast({ title: 'Export Successful', description: 'Online drivers exported to CSV' });
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto" style={{ background: 'rgba(15, 23, 42, 0.98)', borderColor: 'rgba(16, 185, 129, 0.3)' }}>
        <DialogHeader>
          <DialogTitle className="text-white text-2xl flex items-center gap-2">
            <User className="w-6 h-6 text-green-400" />
            Online Drivers ({filteredDrivers.length})
          </DialogTitle>
          <DialogDescription className="text-gray-400">
            Currently active drivers
          </DialogDescription>
        </DialogHeader>

        {/* Filters */}
        <div className="flex gap-4 mb-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Search drivers..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 bg-wheely-navy-900 border-green-500/30 text-white"
              />
            </div>
          </div>
          <Button
            onClick={handleExportCSV}
            className="bg-green-600 hover:bg-green-700"
          >
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
        </div>

        {/* Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {filteredDrivers.map((driver) => (
            <div
              key={driver.id}
              className="p-4 rounded-lg border border-green-500/30"
              style={{ background: 'rgba(15, 23, 42, 0.8)' }}
            >
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse"></div>
                  <h3 className="text-white font-semibold">{driver.name}</h3>
                </div>
                <Badge
                  style={{
                    background: driver.membership_tier === 'gold' ? '#facc15' :
                              driver.membership_tier === 'silver' ? '#94a3b8' : '#EB9868'
                  }}
                >
                  {driver.membership_tier}
                </Badge>
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2 text-gray-300">
                  <Mail className="w-3 h-3" />
                  <span>{driver.location}</span>
                </div>
                <div className="flex justify-between text-gray-400">
                  <span>Rating: ⭐ {driver.rating}/5</span>
                  <span>{driver.total_jobs} jobs</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Driver Verifications Modal Component
export const VerificationsModal = ({ isOpen, onClose, drivers, onRefresh }) => {
  const [rejectingId, setRejectingId] = useState(null);
  const [rejectReason, setRejectReason] = useState('');

  const handleViewDoc = async (driverId, docType) => {
    try {
      await adminAPI.viewVerificationDocument(driverId, docType);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Could not load document',
        variant: 'destructive'
      });
    }
  };

  const handleApprove = async (driverId, name) => {
    try {
      await adminAPI.approveDriver(driverId);
      toast({ title: 'Driver Approved', description: `${name} can now go online` });
      onRefresh();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to approve driver',
        variant: 'destructive'
      });
    }
  };

  const handleReject = async (driverId) => {
    if (!rejectReason.trim()) {
      toast({ title: 'Reason required', description: 'Please explain why this is being rejected', variant: 'destructive' });
      return;
    }
    try {
      await adminAPI.rejectDriver(driverId, rejectReason);
      toast({ title: 'Driver Rejected' });
      setRejectingId(null);
      setRejectReason('');
      onRefresh();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to reject driver',
        variant: 'destructive'
      });
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" style={{ background: 'rgba(15, 23, 42, 0.98)', borderColor: 'rgba(235, 152, 104, 0.3)' }}>
        <DialogHeader>
          <DialogTitle className="text-white text-2xl flex items-center gap-2">
            <Shield className="w-6 h-6 text-wheely-orange-light" />
            Pending Driver Verifications ({drivers.length})
          </DialogTitle>
          <DialogDescription className="text-gray-400">
            Review submitted license & insurance documents before a driver can go online
          </DialogDescription>
        </DialogHeader>

        {drivers.length === 0 ? (
          <p className="text-gray-400 text-center py-8">No pending verifications</p>
        ) : (
          <div className="space-y-4">
            {drivers.map((driver) => (
              <div
                key={driver.id}
                className="p-4 rounded-lg border border-wheely-orange/30"
                style={{ background: 'rgba(15, 23, 42, 0.8)' }}
              >
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-white font-semibold">{driver.name}</h3>
                  <span className="text-gray-400 text-sm">{driver.email}</span>
                </div>
                <p className="text-sm text-gray-400 mb-3">
                  License #: {driver.license_number || 'N/A'} · Submitted{' '}
                  {driver.submitted_at ? new Date(driver.submitted_at).toLocaleDateString() : 'N/A'}
                </p>

                <div className="flex flex-wrap gap-2 mb-3">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleViewDoc(driver.id, 'license_front')}
                    style={{ borderColor: 'rgba(225, 116, 49, 0.4)', color: '#E17431' }}
                  >
                    <FileText className="w-4 h-4 mr-1" /> View License (front)
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleViewDoc(driver.id, 'license_back')}
                    style={{ borderColor: 'rgba(225, 116, 49, 0.4)', color: '#E17431' }}
                  >
                    <FileText className="w-4 h-4 mr-1" /> View License (back)
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleViewDoc(driver.id, 'insurance')}
                    style={{ borderColor: 'rgba(225, 116, 49, 0.4)', color: '#E17431' }}
                  >
                    <FileText className="w-4 h-4 mr-1" /> View Insurance
                  </Button>
                </div>

                {rejectingId === driver.id ? (
                  <div className="flex gap-2">
                    <Input
                      placeholder="Reason for rejection..."
                      value={rejectReason}
                      onChange={(e) => setRejectReason(e.target.value)}
                      className="bg-wheely-navy-900 border-red-500/30 text-white"
                    />
                    <Button size="sm" onClick={() => handleReject(driver.id)} className="bg-red-600 hover:bg-red-700">
                      Confirm
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => { setRejectingId(null); setRejectReason(''); }}>
                      Cancel
                    </Button>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      onClick={() => handleApprove(driver.id, driver.name)}
                      className="bg-green-600 hover:bg-green-700"
                    >
                      <Check className="w-4 h-4 mr-1" /> Approve
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setRejectingId(driver.id)}
                      style={{ borderColor: 'rgba(239, 68, 68, 0.5)', color: '#ef4444' }}
                    >
                      <XCircle className="w-4 h-4 mr-1" /> Reject
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
